import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Moon, Zap, Footprints, Utensils, Target, Plus, Trash2, Loader2,
  Camera, ChevronDown, ChevronUp, TrendingUp, Flame, Droplets,
  SmilePlus, Frown, Meh, Smile, Heart, BarChart3, Clock, X,
  Apple, Dumbbell, BedDouble, Brain, CalendarDays, AlertTriangle,
  MessageSquare, Smartphone, Send, Mic, MicOff, Bell
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, ReferenceLine } from 'recharts';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MOODS = [
  { value: 'happy', label: 'Happy', icon: Smile, color: 'text-emerald-500' },
  { value: 'calm', label: 'Calm', icon: SmilePlus, color: 'text-blue-500' },
  { value: 'neutral', label: 'Neutral', icon: Meh, color: 'text-slate-500' },
  { value: 'anxious', label: 'Anxious', icon: Zap, color: 'text-amber-500' },
  { value: 'stressed', label: 'Stressed', icon: Brain, color: 'text-orange-500' },
  { value: 'sad', label: 'Sad', icon: Frown, color: 'text-rose-500' },
];

const ACTIVITIES = ['Walking', 'Running', 'Cycling', 'Swimming', 'Yoga', 'Gym', 'Sports', 'Hiking', 'Dancing', 'Other'];
const MEALS = ['breakfast', 'lunch', 'dinner', 'snack'];

function getHeaders() {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('patient_token')}` };
}

export default function PatientHealthTracking() {
  const [tab, setTab] = useState('overview');
  const [logs, setLogs] = useState([]);
  const [goals, setGoals] = useState({});
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('7d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [waterCount, setWaterCount] = useState(0);
  const todayStr = new Date().toISOString().split('T')[0];

  // Dialogs
  const [showSleep, setShowSleep] = useState(false);
  const [showStress, setShowStress] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const [showNutrition, setShowNutrition] = useState(false);
  const [showGoals, setShowGoals] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showCheckin, setShowCheckin] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiText, setAiText] = useState('');
  const [aiParsing, setAiParsing] = useState(false);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  const toggleVoice = () => {
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('Voice input not supported in this browser'); return; }
    const recognition = new SR();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-IN';
    recognition.onresult = (e) => {
      const transcript = Array.from(e.results).map(r => r[0].transcript).join('');
      setAiText(transcript);
    };
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  const getDateFrom = useCallback(() => {
    if (dateRange === 'custom') return customFrom;
    const d = new Date();
    if (dateRange === '7d') d.setDate(d.getDate() - 7);
    else if (dateRange === '30d') d.setDate(d.getDate() - 30);
    else if (dateRange === '90d') d.setDate(d.getDate() - 90);
    return d.toISOString().split('T')[0];
  }, [dateRange, customFrom]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const from = getDateFrom();
      const to = dateRange === 'custom' ? customTo : new Date().toISOString().split('T')[0];
      const res = await fetch(`${API_URL}/api/health-tracking/logs?date_from=${from}&date_to=${to}&limit=200`, { headers: getHeaders() });
      if (res.ok) { const d = await res.json(); setLogs(d.logs || []); }
    } catch {}
    setLoading(false);
  }, [getDateFrom, dateRange, customTo]);

  const fetchGoals = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/health-tracking/goals`, { headers: getHeaders() });
      if (res.ok) setGoals(await res.json());
    } catch {}
  }, []);

  useEffect(() => { fetchLogs(); fetchGoals(); }, [fetchLogs, fetchGoals]);

  // Load today's water count from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(`water_${todayStr}`);
    if (saved) setWaterCount(parseInt(saved) || 0);
  }, []);
  const addWater = () => {
    const next = waterCount + 1;
    setWaterCount(next);
    localStorage.setItem(`water_${todayStr}`, next);
  };
  const removeWater = () => {
    const next = Math.max(0, waterCount - 1);
    setWaterCount(next);
    localStorage.setItem(`water_${todayStr}`, next);
  };

  const submitAiLog = async () => {
    if (!aiText.trim()) return;
    setAiParsing(true);
    try {
      const res = await fetch(`${API_URL}/api/health-tracking/ai-log`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify({ text: aiText })
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(`Created ${d.count} log${d.count > 1 ? 's' : ''} from your input`);
        setAiText('');
        fetchLogs();
      } else { toast.error('Could not parse input'); }
    } catch { toast.error('AI parsing failed'); }
    setAiParsing(false);
  };

  const deleteLog = async (id) => {
    try {
      await fetch(`${API_URL}/api/health-tracking/logs/${id}`, { method: 'DELETE', headers: getHeaders() });
      setLogs(prev => prev.filter(l => l.id !== id));
    } catch {}
  };

  // Computed data
  const sleepLogs = useMemo(() => logs.filter(l => l.type === 'sleep'), [logs]);
  const stressLogs = useMemo(() => logs.filter(l => l.type === 'stress'), [logs]);
  const activityLogs = useMemo(() => logs.filter(l => l.type === 'activity'), [logs]);
  const nutritionLogs = useMemo(() => logs.filter(l => l.type === 'nutrition'), [logs]);

  const todayActivity = activityLogs.filter(l => l.date === todayStr);
  const todayNutrition = nutritionLogs.filter(l => l.date === todayStr);
  const todaySleep = sleepLogs.find(l => l.date === todayStr);
  const todaySteps = todayActivity.reduce((s, l) => s + (l.steps || 0), 0);
  const todayCalsBurned = todayActivity.reduce((s, l) => s + (l.calories_burned || 0), 0);
  const todayCalsIntake = todayNutrition.reduce((s, l) => s + (l.calories || 0), 0);

  const TABS = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'calendar', label: 'Calendar', icon: Clock },
    { id: 'sleep', label: 'Sleep', icon: Moon },
    { id: 'stress', label: 'Stress', icon: Brain },
    { id: 'activity', label: 'Activity', icon: Dumbbell },
    { id: 'nutrition', label: 'Nutrition', icon: Apple },
    { id: 'profile', label: 'Medical Profile', icon: Heart },
  ];

  return (
    <div className="space-y-4" data-testid="health-tracking">
      {/* Tab bar */}
      <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${tab === t.id ? 'bg-teal-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            data-testid={`ht-tab-${t.id}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* Quick Entry Options */}
      {tab === 'overview' && (
        <div className="space-y-3">
          {/* AI Natural Language Input */}
          <Card className="border-teal-200 bg-teal-50/50" data-testid="ai-log-card">
            <CardContent className="p-3">
              <p className="text-xs font-bold text-teal-700 mb-2 flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> AI Quick Log — Type or speak naturally</p>
              <div className="flex gap-2">
                <Input
                  data-testid="ai-log-input"
                  value={aiText}
                  onChange={e => setAiText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && submitAiLog()}
                  placeholder={listening ? 'Listening...' : 'e.g. Slept 7h, walked 30min, had rice dal for lunch ~500cal'}
                  className={`flex-1 h-9 text-xs bg-white ${listening ? 'border-rose-400 ring-1 ring-rose-300' : ''}`}
                />
                <Button size="sm" onClick={toggleVoice} variant={listening ? 'default' : 'outline'} className={`h-9 w-9 p-0 ${listening ? 'bg-rose-600 hover:bg-rose-700 text-white animate-pulse' : ''}`} data-testid="voice-input-btn" title="Voice input">
                  {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </Button>
                <Button size="sm" onClick={submitAiLog} disabled={aiParsing || !aiText.trim()} className="bg-teal-700 hover:bg-teal-800 text-white h-9 px-3" data-testid="ai-log-submit">
                  {aiParsing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-[10px] text-teal-600 mt-1">AI will auto-create sleep, mood, activity & nutrition logs from your text</p>
            </CardContent>
          </Card>

          {/* Quick Actions Row */}
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setShowCheckin(true)} className="flex-1 h-10 bg-indigo-600 hover:bg-indigo-700 text-white text-xs" data-testid="quick-checkin-btn">
              <Zap className="w-4 h-4 mr-1.5" /> Daily Check-in
            </Button>
          </div>
        </div>
      )}

      {/* Date Range */}
      {tab !== 'profile' && tab !== 'connected' && (
        <div className="flex items-center gap-2 flex-wrap">
          {['7d', '30d', '90d', 'custom'].map(r => (
            <button key={r} onClick={() => setDateRange(r)}
              className={`px-2.5 py-1 rounded text-[11px] font-medium ${dateRange === r ? 'bg-teal-100 text-teal-800' : 'bg-slate-100 text-slate-500'}`}>
              {r === '7d' ? '7 Days' : r === '30d' ? '30 Days' : r === '90d' ? '90 Days' : 'Custom'}
            </button>
          ))}
          {dateRange === 'custom' && (
            <>
              <Input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="h-7 w-32 text-xs" />
              <span className="text-xs text-slate-400">to</span>
              <Input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="h-7 w-32 text-xs" />
              <Button size="sm" className="h-7 text-xs" onClick={fetchLogs}>Apply</Button>
            </>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal-600 mx-auto" /></div>
      ) : (
        <>
          {tab === 'overview' && <OverviewTab todaySteps={todaySteps} todayCalsBurned={todayCalsBurned} todayCalsIntake={todayCalsIntake} todaySleep={todaySleep} goals={goals} sleepLogs={sleepLogs} activityLogs={activityLogs} nutritionLogs={nutritionLogs} stressLogs={stressLogs} waterCount={waterCount} addWater={addWater} removeWater={removeWater} onGoals={() => setShowGoals(true)} />}
          {tab === 'calendar' && <CalendarView />}
          {tab === 'sleep' && <LogList logs={sleepLogs} type="sleep" onAdd={() => setShowSleep(true)} onDelete={deleteLog} />}
          {tab === 'stress' && <LogList logs={stressLogs} type="stress" onAdd={() => setShowStress(true)} onDelete={deleteLog} />}
          {tab === 'activity' && <LogList logs={activityLogs} type="activity" onAdd={() => setShowActivity(true)} onDelete={deleteLog} />}
          {tab === 'nutrition' && <NutritionTab logs={nutritionLogs} onAdd={() => setShowNutrition(true)} onDelete={deleteLog} />}
          {tab === 'profile' && <MedicalProfile />}
        </>
      )}

      {/* Quick Add FAB */}
      {tab !== 'profile' && (
        <div className="fixed bottom-20 right-4 flex flex-col gap-2 z-40">
          <Button size="sm" className="rounded-full w-10 h-10 bg-indigo-600 hover:bg-indigo-700 shadow-lg" onClick={() => setShowSleep(true)} title="Log Sleep"><Moon className="w-4 h-4" /></Button>
          <Button size="sm" className="rounded-full w-10 h-10 bg-amber-600 hover:bg-amber-700 shadow-lg" onClick={() => setShowStress(true)} title="Log Mood"><Brain className="w-4 h-4" /></Button>
          <Button size="sm" className="rounded-full w-10 h-10 bg-emerald-600 hover:bg-emerald-700 shadow-lg" onClick={() => setShowActivity(true)} title="Log Activity"><Dumbbell className="w-4 h-4" /></Button>
          <Button size="sm" className="rounded-full w-10 h-10 bg-orange-600 hover:bg-orange-700 shadow-lg" onClick={() => setShowNutrition(true)} title="Log Meal"><Utensils className="w-4 h-4" /></Button>
        </div>
      )}

      {/* Sleep Dialog */}
      <LogDialog open={showSleep} onClose={() => setShowSleep(false)} title="Log Sleep" icon={Moon} saving={saving} onSave={async (d) => {
        setSaving(true);
        try {
          const res = await fetch(`${API_URL}/api/health-tracking/sleep`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(d) });
          if (res.ok) { toast.success('Sleep logged'); setShowSleep(false); fetchLogs(); }
        } catch {} setSaving(false);
      }}>
        {(form, setForm) => (<>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Date</Label><Input type="date" value={form.date || todayStr} onChange={e => setForm({ ...form, date: e.target.value })} className="h-9 mt-0.5" /></div>
            <div><Label className="text-xs">Hours Slept</Label><Input type="number" step="0.5" value={form.sleep_hours || ''} onChange={e => setForm({ ...form, sleep_hours: parseFloat(e.target.value) || null })} placeholder="7.5" className="h-9 mt-0.5" /></div>
            <div><Label className="text-xs">Bed Time</Label><Input type="time" value={form.bed_time || ''} onChange={e => setForm({ ...form, bed_time: e.target.value })} className="h-9 mt-0.5" /></div>
            <div><Label className="text-xs">Wake Time</Label><Input type="time" value={form.wake_time || ''} onChange={e => setForm({ ...form, wake_time: e.target.value })} className="h-9 mt-0.5" /></div>
          </div>
          <div><Label className="text-xs">Sleep Quality</Label>
            <div className="flex gap-1 mt-1">{[1,2,3,4,5].map(q => (
              <button key={q} onClick={() => setForm({ ...form, sleep_quality: q })} className={`w-9 h-9 rounded-lg text-sm font-bold ${form.sleep_quality === q ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600'}`}>{q}</button>
            ))}</div>
          </div>
        </>)}
      </LogDialog>

      {/* Stress Dialog */}
      <LogDialog open={showStress} onClose={() => setShowStress(false)} title="Log Mood & Stress" icon={Brain} saving={saving} onSave={async (d) => {
        setSaving(true);
        try {
          const res = await fetch(`${API_URL}/api/health-tracking/stress`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(d) });
          if (res.ok) { toast.success('Mood logged'); setShowStress(false); fetchLogs(); }
        } catch {} setSaving(false);
      }}>
        {(form, setForm) => (<>
          <div><Label className="text-xs">Date</Label><Input type="date" value={form.date || todayStr} onChange={e => setForm({ ...form, date: e.target.value })} className="h-9 mt-0.5" /></div>
          <div><Label className="text-xs">Mood</Label>
            <div className="flex gap-2 mt-1 flex-wrap">{MOODS.map(m => (
              <button key={m.value} onClick={() => setForm({ ...form, mood: m.value })} className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border ${form.mood === m.value ? 'border-teal-500 bg-teal-50' : 'border-slate-200'}`}>
                <m.icon className={`w-3.5 h-3.5 ${m.color}`} /> {m.label}
              </button>
            ))}</div>
          </div>
          <div><Label className="text-xs">Stress Level (1-10)</Label>
            <div className="flex gap-1 mt-1">{[1,2,3,4,5,6,7,8,9,10].map(l => (
              <button key={l} onClick={() => setForm({ ...form, stress_level: l })} className={`w-7 h-7 rounded text-[10px] font-bold ${form.stress_level === l ? (l <= 3 ? 'bg-emerald-600 text-white' : l <= 6 ? 'bg-amber-500 text-white' : 'bg-rose-600 text-white') : 'bg-slate-100 text-slate-600'}`}>{l}</button>
            ))}</div>
          </div>
          <div><Label className="text-xs">Triggers / Notes</Label><Input value={form.triggers || ''} onChange={e => setForm({ ...form, triggers: e.target.value })} placeholder="What caused it?" className="h-9 mt-0.5" /></div>
        </>)}
      </LogDialog>

      {/* Activity Dialog */}
      <LogDialog open={showActivity} onClose={() => setShowActivity(false)} title="Log Activity" icon={Dumbbell} saving={saving} onSave={async (d) => {
        setSaving(true);
        try {
          const res = await fetch(`${API_URL}/api/health-tracking/activity`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(d) });
          if (res.ok) { toast.success('Activity logged'); setShowActivity(false); fetchLogs(); }
        } catch {} setSaving(false);
      }}>
        {(form, setForm) => (<>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Date</Label><Input type="date" value={form.date || todayStr} onChange={e => setForm({ ...form, date: e.target.value })} className="h-9 mt-0.5" /></div>
            <div><Label className="text-xs">Activity</Label>
              <select value={form.activity_type || 'Walking'} onChange={e => setForm({ ...form, activity_type: e.target.value })} className="w-full h-9 mt-0.5 rounded-md border border-input bg-background px-3 text-sm">
                {ACTIVITIES.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Duration (min)</Label><Input type="number" value={form.duration_minutes || ''} onChange={e => setForm({ ...form, duration_minutes: parseInt(e.target.value) || null })} placeholder="30" className="h-9 mt-0.5" /></div>
            <div><Label className="text-xs">Calories Burned</Label><Input type="number" value={form.calories_burned || ''} onChange={e => setForm({ ...form, calories_burned: parseInt(e.target.value) || null })} placeholder="200" className="h-9 mt-0.5" /></div>
            <div><Label className="text-xs">Steps</Label><Input type="number" value={form.steps || ''} onChange={e => setForm({ ...form, steps: parseInt(e.target.value) || null })} placeholder="5000" className="h-9 mt-0.5" /></div>
            <div><Label className="text-xs">Distance (km)</Label><Input type="number" step="0.1" value={form.distance_km || ''} onChange={e => setForm({ ...form, distance_km: parseFloat(e.target.value) || null })} placeholder="3.5" className="h-9 mt-0.5" /></div>
          </div>
        </>)}
      </LogDialog>

      {/* Nutrition Dialog */}
      <NutritionDialog open={showNutrition} onClose={() => setShowNutrition(false)} onSave={async (d) => {
        setSaving(true);
        try {
          const res = await fetch(`${API_URL}/api/health-tracking/nutrition`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(d) });
          if (res.ok) { toast.success('Meal logged'); setShowNutrition(false); fetchLogs(); }
        } catch {} setSaving(false);
      }} saving={saving} />

      {/* Goals Dialog */}
      <GoalsDialog open={showGoals} onClose={() => setShowGoals(false)} goals={goals} onSave={async (d) => {
        setSaving(true);
        try {
          const res = await fetch(`${API_URL}/api/health-tracking/goals`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(d) });
          if (res.ok) { toast.success('Goals saved'); setShowGoals(false); fetchGoals(); }
        } catch {} setSaving(false);
      }} saving={saving} />

      {/* Quick Daily Check-in Dialog */}
      <QuickCheckinDialog open={showCheckin} onClose={() => setShowCheckin(false)} onSave={async (d) => {
        setSaving(true);
        try {
          const res = await fetch(`${API_URL}/api/health-tracking/quick-checkin`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(d) });
          if (res.ok) { const r = await res.json(); toast.success(`${r.count} entries logged!`); setShowCheckin(false); fetchLogs(); }
        } catch {} setSaving(false);
      }} saving={saving} />
    </div>
  );
}

// ==================== OVERVIEW ====================
function OverviewTab({ todaySteps, todayCalsBurned, todayCalsIntake, todaySleep, goals, sleepLogs, activityLogs, nutritionLogs, stressLogs, waterCount, addWater, removeWater, onGoals }) {
  const sleepChart = [...sleepLogs].reverse().slice(-14).map(l => ({ date: l.date?.slice(5), hrs: l.sleep_hours }));
  const stressChart = [...stressLogs].reverse().slice(-14).map(l => ({ date: l.date?.slice(5), level: l.stress_level }));

  return (
    <div className="space-y-4">
      {/* Goals button */}
      <Button variant="outline" size="sm" onClick={onGoals} className="w-full" data-testid="set-goals-btn"><Target className="w-4 h-4 mr-1" /> Set Health Goals</Button>

      {/* Today cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Footprints} label="Steps" value={todaySteps} target={goals.daily_steps} color="emerald" />
        <StatCard icon={Flame} label="Burned" value={`${todayCalsBurned} cal`} target={goals.daily_calories_burn} suffix=" cal" color="orange" />
        <StatCard icon={Utensils} label="Intake" value={`${todayCalsIntake} cal`} target={goals.daily_calories_intake} suffix=" cal" color="blue" />
        <StatCard icon={BedDouble} label="Sleep" value={todaySleep ? `${todaySleep.sleep_hours}h` : '--'} target={goals.sleep_hours} suffix="h" color="indigo" />
      </div>

      {/* Water Tracker */}
      <Card className="border-slate-200" data-testid="water-tracker">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-bold text-slate-700 flex items-center gap-1.5"><Droplets className="w-4 h-4 text-blue-500" /> Water Intake</p>
            <p className="text-xs text-slate-500">{waterCount} / {goals.water_glasses || 8} glasses</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 flex gap-1 flex-wrap">
              {Array.from({ length: goals.water_glasses || 8 }).map((_, i) => (
                <div key={i} className={`w-7 h-9 rounded-md border-2 flex items-center justify-center transition-colors ${i < waterCount ? 'bg-blue-500 border-blue-500' : 'bg-blue-50 border-blue-200'}`}>
                  <Droplets className={`w-3.5 h-3.5 ${i < waterCount ? 'text-white' : 'text-blue-300'}`} />
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-1">
              <Button size="sm" className="h-8 w-8 rounded-full bg-blue-600 hover:bg-blue-700 text-white p-0" onClick={addWater} data-testid="add-water-btn">+</Button>
              <Button size="sm" variant="outline" className="h-8 w-8 rounded-full p-0 text-xs" onClick={removeWater} data-testid="remove-water-btn">-</Button>
            </div>
          </div>
          {waterCount >= (goals.water_glasses || 8) && (
            <p className="text-xs text-emerald-600 mt-2 font-medium">Daily water goal reached!</p>
          )}
        </CardContent>
      </Card>
      {/* Charts */}
      {sleepChart.length > 1 && (
        <Card className="border-slate-200"><CardContent className="p-3">
          <p className="text-xs font-bold text-slate-600 mb-2">Sleep Hours Trend <span className="font-normal text-[10px] text-emerald-600">(green = 8h goal)</span></p>
          <ResponsiveContainer width="100%" height={150}>
            <BarChart data={sleepChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <ReferenceLine y={8} stroke="#2BB673" strokeDasharray="4 4" label={{ value: 'Goal', position: 'right', fontSize: 9, fill: '#2BB673' }} />
              <Bar dataKey="hrs" fill="#6366f1" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent></Card>
      )}
      {stressChart.length > 1 && (
        <Card className="border-slate-200"><CardContent className="p-3">
          <p className="text-xs font-bold text-slate-600 mb-2">Stress Level Trend <span className="font-normal text-[10px] text-amber-600">(amber = elevated)</span></p>
          <ResponsiveContainer width="100%" height={150}>
            <LineChart data={stressChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis domain={[0,10]} tick={{ fontSize: 10 }} />
              <Tooltip />
              <ReferenceLine y={6} stroke="#F59E0B" strokeDasharray="4 4" label={{ value: 'Watch', position: 'right', fontSize: 9, fill: '#F59E0B' }} />
              <Line type="monotone" dataKey="level" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent></Card>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, target, color }) {
  return (
    <Card className="border-slate-200"><CardContent className="p-3 text-center">
      <Icon className={`w-5 h-5 mx-auto mb-1 text-${color}-600`} />
      <p className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>{value}</p>
      <p className="text-[10px] text-slate-500">{label}{target ? ` / ${target}` : ''}</p>
    </CardContent></Card>
  );
}

function SourceBadge({ source }) {
  if (source === 'google_fit') return <Badge className="bg-blue-100 text-blue-700 text-[9px] ml-1 gap-0.5"><Heart className="w-2.5 h-2.5" />Google Fit</Badge>;
  if (source === 'ai_parsed') return <Badge className="bg-teal-100 text-teal-700 text-[9px] ml-1">AI</Badge>;
  if (source === 'quick_checkin') return <Badge className="bg-indigo-100 text-indigo-700 text-[9px] ml-1">Quick</Badge>;
  return null;
}

// ==================== LOG LIST ====================
function LogList({ logs, type, onAdd, onDelete }) {
  const icon = type === 'sleep' ? Moon : type === 'stress' ? Brain : Dumbbell;
  const Icon = icon;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-700">{type === 'sleep' ? 'Sleep' : type === 'stress' ? 'Mood & Stress' : 'Activity'} Log ({logs.length})</h3>
        <Button size="sm" onClick={onAdd} className="bg-teal-700 hover:bg-teal-800 text-white h-8"><Plus className="w-3 h-3 mr-1" />Add</Button>
      </div>
      {logs.length === 0 ? (
        <div className="text-center py-8 text-slate-400"><Icon className="w-8 h-8 mx-auto mb-2 opacity-30" /><p className="text-sm">No entries yet</p></div>
      ) : logs.map(l => (
        <Card key={l.id} className="border-slate-200"><CardContent className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">{l.date}<SourceBadge source={l.source} /></p>
              {type === 'sleep' && <p className="text-sm font-bold text-slate-800">{l.sleep_hours}h sleep <span className="text-xs text-slate-500">Quality: {l.sleep_quality}/5</span></p>}
              {type === 'stress' && <p className="text-sm font-bold text-slate-800">Stress: {l.stress_level}/10 <Badge variant="outline" className="text-[10px] ml-1 capitalize">{l.mood}</Badge>{l.triggers && <span className="text-xs text-slate-500 ml-2">{l.triggers}</span>}</p>}
              {type === 'activity' && <p className="text-sm font-bold text-slate-800">{l.activity_type} - {l.duration_minutes}min {l.calories_burned && <span className="text-xs text-orange-600">{l.calories_burned} cal</span>} {l.steps && <span className="text-xs text-emerald-600">{l.steps} steps</span>}</p>}
            </div>
            <button onClick={() => onDelete(l.id)} className="text-slate-400 hover:text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        </CardContent></Card>
      ))}
    </div>
  );
}

// ==================== NUTRITION TAB ====================
function NutritionTab({ logs, onAdd, onDelete }) {
  const byDate = {};
  logs.forEach(l => { if (!byDate[l.date]) byDate[l.date] = []; byDate[l.date].push(l); });
  const dates = Object.keys(byDate).sort().reverse();

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-700">Nutrition Log ({logs.length})</h3>
        <Button size="sm" onClick={onAdd} className="bg-teal-700 hover:bg-teal-800 text-white h-8"><Plus className="w-3 h-3 mr-1" />Add Meal</Button>
      </div>
      {dates.length === 0 ? (
        <div className="text-center py-8 text-slate-400"><Apple className="w-8 h-8 mx-auto mb-2 opacity-30" /><p className="text-sm">No meals logged yet</p></div>
      ) : dates.map(date => {
        const dayLogs = byDate[date];
        const totalCal = dayLogs.reduce((s, l) => s + (l.calories || 0), 0);
        return (
          <Card key={date} className="border-slate-200"><CardContent className="p-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold text-slate-700">{date}</p>
              <Badge className="bg-orange-100 text-orange-700 text-[10px]">{totalCal} cal total</Badge>
            </div>
            {dayLogs.map(l => (
              <div key={l.id} className="flex items-center justify-between py-1 border-t border-slate-100 first:border-0">
                <div>
                  <p className="text-sm text-slate-800"><Badge variant="outline" className="text-[10px] capitalize mr-1">{l.meal_type}</Badge>{l.food_items}<SourceBadge source={l.source} /></p>
                  <p className="text-[10px] text-slate-500">{l.calories && `${l.calories} cal`} {l.protein_g && `P:${l.protein_g}g`} {l.carbs_g && `C:${l.carbs_g}g`} {l.fat_g && `F:${l.fat_g}g`}</p>
                </div>
                <button onClick={() => onDelete(l.id)} className="text-slate-400 hover:text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </CardContent></Card>
        );
      })}
    </div>
  );
}

// ==================== NUTRITION DIALOG WITH FOOD SCAN ====================
function NutritionDialog({ open, onClose, onSave, saving }) {
  const [form, setForm] = useState({});
  const [scanning, setScanning] = useState(false);
  const fileRef = useRef(null);
  const todayStr = new Date().toISOString().split('T')[0];

  const scanFood = async (base64) => {
    setScanning(true);
    try {
      const res = await fetch(`${API_URL}/api/health-tracking/nutrition/scan-food`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify({ image: base64 })
      });
      if (res.ok) {
        const data = await res.json();
        setForm(prev => ({
          ...prev,
          food_items: data.food_items || prev.food_items,
          meal_type: data.meal_type || prev.meal_type,
          calories: data.total_calories || prev.calories,
          protein_g: data.protein_g || prev.protein_g,
          carbs_g: data.carbs_g || prev.carbs_g,
          fat_g: data.fat_g || prev.fat_g,
          fiber_g: data.fiber_g || prev.fiber_g,
          ai_analysis: data,
        }));
        toast.success('Food analyzed!');
      } else { toast.error('Scan failed'); }
    } catch { toast.error('Scan failed'); }
    setScanning(false);
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => scanFood(reader.result);
    reader.readAsDataURL(file);
  };

  const handleCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      const video = document.createElement('video');
      video.srcObject = stream;
      await video.play();
      await new Promise(r => setTimeout(r, 1000));
      const c = document.createElement('canvas');
      c.width = video.videoWidth; c.height = video.videoHeight;
      c.getContext('2d').drawImage(video, 0, 0);
      stream.getTracks().forEach(t => t.stop());
      scanFood(c.toDataURL('image/jpeg', 0.8));
    } catch { toast.error('Camera access denied'); }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Utensils className="w-5 h-5 text-orange-600" /> Log Meal</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {/* AI Scan buttons */}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1" onClick={handleCamera} disabled={scanning} data-testid="scan-food-camera">
              {scanning ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Camera className="w-4 h-4 mr-1" />} Scan Food
            </Button>
            <Button variant="outline" size="sm" className="flex-1" onClick={() => fileRef.current?.click()} disabled={scanning} data-testid="upload-food-image">
              {scanning ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />} Upload Photo
            </Button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
          </div>
          {scanning && <p className="text-xs text-center text-teal-600 animate-pulse">Analyzing food image with AI...</p>}
          {form.ai_analysis && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2">
              <p className="text-xs text-emerald-700">{form.ai_analysis.health_notes}</p>
              <p className="text-[10px] text-emerald-600 mt-1">AI Confidence: {form.ai_analysis.confidence}%</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Date</Label><Input type="date" value={form.date || todayStr} onChange={e => setForm({ ...form, date: e.target.value })} className="h-9 mt-0.5" /></div>
            <div><Label className="text-xs">Meal</Label>
              <select value={form.meal_type || 'snack'} onChange={e => setForm({ ...form, meal_type: e.target.value })} className="w-full h-9 mt-0.5 rounded-md border border-input bg-background px-3 text-sm">
                {MEALS.map(m => <option key={m} value={m} className="capitalize">{m}</option>)}
              </select>
            </div>
          </div>
          <div><Label className="text-xs">Food Items</Label><Input value={form.food_items || ''} onChange={e => setForm({ ...form, food_items: e.target.value })} placeholder="e.g. Rice, Dal, Salad" className="h-9 mt-0.5" /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Calories</Label><Input type="number" value={form.calories || ''} onChange={e => setForm({ ...form, calories: parseInt(e.target.value) || null })} placeholder="500" className="h-9 mt-0.5" /></div>
            <div><Label className="text-xs">Protein (g)</Label><Input type="number" value={form.protein_g || ''} onChange={e => setForm({ ...form, protein_g: parseInt(e.target.value) || null })} className="h-9 mt-0.5" /></div>
            <div><Label className="text-xs">Carbs (g)</Label><Input type="number" value={form.carbs_g || ''} onChange={e => setForm({ ...form, carbs_g: parseInt(e.target.value) || null })} className="h-9 mt-0.5" /></div>
            <div><Label className="text-xs">Fat (g)</Label><Input type="number" value={form.fat_g || ''} onChange={e => setForm({ ...form, fat_g: parseInt(e.target.value) || null })} className="h-9 mt-0.5" /></div>
            <div><Label className="text-xs">Fiber (g)</Label><Input type="number" value={form.fiber_g || ''} onChange={e => setForm({ ...form, fiber_g: parseInt(e.target.value) || null })} className="h-9 mt-0.5" /></div>
          </div>
          <Button onClick={() => onSave(form)} disabled={saving} className="w-full bg-teal-700 hover:bg-teal-800 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Save Meal
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ==================== LOG DIALOG WRAPPER ====================
function LogDialog({ open, onClose, title, icon: Icon, children, onSave, saving }) {
  const [form, setForm] = useState({});
  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { onClose(); setForm({}); } }}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Icon className="w-5 h-5 text-teal-600" /> {title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {children(form, setForm)}
          <Button onClick={() => onSave(form)} disabled={saving} className="w-full bg-teal-700 hover:bg-teal-800 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ==================== GOALS DIALOG ====================
function GoalsDialog({ open, onClose, goals, onSave, saving }) {
  const [form, setForm] = useState(goals);
  useEffect(() => { setForm(goals); }, [goals]);
  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Target className="w-5 h-5 text-teal-600" /> Health Goals</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Daily Steps</Label><Input type="number" value={form.daily_steps || ''} onChange={e => setForm({ ...form, daily_steps: parseInt(e.target.value) || null })} className="h-9 mt-0.5" /></div>
            <div><Label className="text-xs">Sleep Hours</Label><Input type="number" step="0.5" value={form.sleep_hours || ''} onChange={e => setForm({ ...form, sleep_hours: parseFloat(e.target.value) || null })} className="h-9 mt-0.5" /></div>
            <div><Label className="text-xs">Cal Intake</Label><Input type="number" value={form.daily_calories_intake || ''} onChange={e => setForm({ ...form, daily_calories_intake: parseInt(e.target.value) || null })} className="h-9 mt-0.5" /></div>
            <div><Label className="text-xs">Cal Burn</Label><Input type="number" value={form.daily_calories_burn || ''} onChange={e => setForm({ ...form, daily_calories_burn: parseInt(e.target.value) || null })} className="h-9 mt-0.5" /></div>
            <div><Label className="text-xs">Water (glasses)</Label><Input type="number" value={form.water_glasses || ''} onChange={e => setForm({ ...form, water_glasses: parseInt(e.target.value) || null })} className="h-9 mt-0.5" /></div>
            <div><Label className="text-xs">Weight Target (kg)</Label><Input type="number" step="0.5" value={form.weight_target_kg || ''} onChange={e => setForm({ ...form, weight_target_kg: parseFloat(e.target.value) || null })} className="h-9 mt-0.5" /></div>
          </div>
          <Button onClick={() => onSave(form)} disabled={saving} className="w-full bg-teal-700 hover:bg-teal-800 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Save Goals
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ==================== MEDICAL PROFILE ====================
function MedicalProfile() {
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/health-tracking/medical-profile`, { headers: getHeaders() });
        if (res.ok) setProfile(await res.json());
      } catch {}
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/health-tracking/medical-profile`, { method: 'PUT', headers: getHeaders(), body: JSON.stringify(profile) });
      if (res.ok) toast.success('Medical profile updated');
    } catch {}
    setSaving(false);
  };

  if (loading) return <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin text-teal-600 mx-auto" /></div>;

  const fields = [
    { key: 'allergies', label: 'Allergies', placeholder: 'e.g. Penicillin, Peanuts, Dust' },
    { key: 'current_medications', label: 'Current Medications', placeholder: 'e.g. Metformin 500mg, Amlodipine 5mg' },
    { key: 'chronic_conditions', label: 'Chronic Conditions', placeholder: 'e.g. Diabetes Type 2, Hypertension' },
    { key: 'past_surgeries', label: 'Past Surgeries', placeholder: 'e.g. Appendectomy (2020), Knee replacement (2022)' },
    { key: 'immunizations', label: 'Immunizations', placeholder: 'e.g. COVID-19 (3 doses), Flu (2025), Hepatitis B' },
    { key: 'family_history', label: 'Family Medical History', placeholder: 'e.g. Father: Diabetes, Mother: Hypertension' },
    { key: 'lifestyle_notes', label: 'Lifestyle Notes', placeholder: 'e.g. Non-smoker, Vegetarian, Exercises 3x/week' },
  ];

  return (
    <Card className="border-slate-200" data-testid="medical-profile-form">
      <CardHeader className="pb-2"><CardTitle className="text-base">Structured Medical Profile</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {fields.map(f => (
          <div key={f.key}>
            <Label className="text-xs font-medium">{f.label}</Label>
            <Input value={profile[f.key] || ''} onChange={e => setProfile({ ...profile, [f.key]: e.target.value })} placeholder={f.placeholder} className="h-9 mt-0.5" data-testid={`mp-${f.key}`} />
          </div>
        ))}
        <Button onClick={save} disabled={saving} className="w-full bg-teal-700 hover:bg-teal-800 text-white" data-testid="mp-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Save Medical Profile
        </Button>
      </CardContent>
    </Card>
  );
}


// ==================== CALENDAR VIEW ====================
function CalendarView() {
  const [month, setMonth] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; });
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recs, setRecs] = useState([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [calRes, recRes] = await Promise.all([
          fetch(`${API_URL}/api/health-tracking/calendar?month=${month}`, { headers: getHeaders() }),
          fetch(`${API_URL}/api/health-tracking/recommendations`, { headers: getHeaders() })
        ]);
        if (calRes.ok) { const d = await calRes.json(); setDays(d.days || []); }
        if (recRes.ok) { const d = await recRes.json(); setRecs(d.recommendations || []); }
      } catch {}
      setLoading(false);
    })();
  }, [month]);

  const year = parseInt(month.split('-')[0]);
  const mo = parseInt(month.split('-')[1]) - 1;
  const daysInMonth = new Date(year, mo + 1, 0).getDate();
  const firstDay = new Date(year, mo, 1).getDay();
  const dayMap = {};
  days.forEach(d => { dayMap[d.date] = d; });

  const prevMonth = () => { const d = new Date(year, mo - 1, 1); setMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`); };
  const nextMonth = () => { const d = new Date(year, mo + 1, 1); setMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`); };

  return (
    <div className="space-y-4" data-testid="calendar-view">
      {/* Doctor Recommendations */}
      {recs.length > 0 && (
        <Card className="border-indigo-200 bg-indigo-50"><CardContent className="p-3">
          <p className="text-xs font-bold text-indigo-700 mb-2 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Doctor's Health Targets</p>
          {recs.map((r, i) => (
            <div key={i} className="text-xs text-indigo-600 space-y-0.5">
              <p className="font-medium">Dr. {r.doctor_name}</p>
              <div className="flex flex-wrap gap-3">
                {r.daily_calories_intake && <span>Calories: {r.daily_calories_intake}/day</span>}
                {r.daily_water_glasses && <span>Water: {r.daily_water_glasses} glasses</span>}
                {r.daily_steps && <span>Steps: {r.daily_steps}</span>}
                {r.sleep_hours && <span>Sleep: {r.sleep_hours}h</span>}
              </div>
              {r.restrictions && <p className="text-rose-600">Restrictions: {r.restrictions}</p>}
              {r.notes && <p className="text-slate-600">{r.notes}</p>}
            </div>
          ))}
        </CardContent></Card>
      )}

      {/* Calendar */}
      <Card className="border-slate-200"><CardContent className="p-3">
        <div className="flex items-center justify-between mb-3">
          <Button variant="ghost" size="sm" onClick={prevMonth}>&lt;</Button>
          <p className="text-sm font-bold text-slate-700">{new Date(year, mo).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</p>
          <Button variant="ghost" size="sm" onClick={nextMonth}>&gt;</Button>
        </div>
        {loading ? <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin text-teal-600 mx-auto" /></div> : (
          <>
            <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-slate-400 mb-1">
              {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => <div key={d}>{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} />)}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const dateStr = `${month}-${String(day).padStart(2, '0')}`;
                const data = dayMap[dateStr];
                const hasData = data && data.total > 0;
                const isToday = dateStr === new Date().toISOString().split('T')[0];
                return (
                  <div key={day} className={`aspect-square rounded-lg flex flex-col items-center justify-center text-xs relative ${isToday ? 'ring-2 ring-teal-500' : ''} ${hasData ? 'bg-teal-50 border border-teal-200' : 'bg-slate-50 border border-slate-100'}`}>
                    <span className={`font-medium ${hasData ? 'text-teal-800' : 'text-slate-400'}`}>{day}</span>
                    {hasData && (
                      <div className="flex gap-0.5 mt-0.5">
                        {data.sleep > 0 && <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" title="Sleep" />}
                        {data.stress > 0 && <div className="w-1.5 h-1.5 rounded-full bg-amber-500" title="Stress" />}
                        {data.activity > 0 && <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" title="Activity" />}
                        {data.nutrition > 0 && <div className="w-1.5 h-1.5 rounded-full bg-orange-500" title="Nutrition" />}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex gap-3 mt-3 text-[10px] text-slate-500 justify-center">
              <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-indigo-500" />Sleep</span>
              <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-amber-500" />Stress</span>
              <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-emerald-500" />Activity</span>
              <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-orange-500" />Nutrition</span>
            </div>
          </>
        )}
      </CardContent></Card>
    </div>
  );
}


// ==================== QUICK DAILY CHECK-IN ====================
function QuickCheckinDialog({ open, onClose, onSave, saving }) {
  const [f, setF] = useState({});
  const todayStr = new Date().toISOString().split('T')[0];

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { onClose(); setF({}); } }}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-indigo-600" /> Daily Check-in</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-slate-500">Log everything in one go — fill what applies, skip the rest</p>

          {/* Sleep */}
          <div className="bg-indigo-50 rounded-lg p-3 space-y-2">
            <p className="text-xs font-bold text-indigo-700 flex items-center gap-1"><Moon className="w-3.5 h-3.5" /> Sleep</p>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-[10px]">Hours</Label><Input type="number" step="0.5" value={f.sleep_hours||''} onChange={e => setF({...f, sleep_hours: parseFloat(e.target.value)||null})} placeholder="7.5" className="h-8 text-xs mt-0.5" /></div>
              <div><Label className="text-[10px]">Quality (1-5)</Label>
                <div className="flex gap-1 mt-0.5">{[1,2,3,4,5].map(q => (
                  <button key={q} onClick={() => setF({...f, sleep_quality: q})} className={`w-7 h-8 rounded text-xs font-bold ${f.sleep_quality===q ? 'bg-indigo-600 text-white' : 'bg-white border text-slate-500'}`}>{q}</button>
                ))}</div>
              </div>
            </div>
          </div>

          {/* Mood */}
          <div className="bg-amber-50 rounded-lg p-3 space-y-2">
            <p className="text-xs font-bold text-amber-700 flex items-center gap-1"><Brain className="w-3.5 h-3.5" /> Mood & Stress</p>
            <div className="flex gap-1.5 flex-wrap">
              {MOODS.map(m => (
                <button key={m.value} onClick={() => setF({...f, mood: m.value})} className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] border ${f.mood===m.value ? 'border-amber-500 bg-amber-100' : 'border-slate-200 bg-white'}`}>
                  <m.icon className={`w-3 h-3 ${m.color}`} /> {m.label}
                </button>
              ))}
            </div>
            <div><Label className="text-[10px]">Stress (1-10)</Label>
              <div className="flex gap-0.5 mt-0.5">{[1,2,3,4,5,6,7,8,9,10].map(l => (
                <button key={l} onClick={() => setF({...f, stress_level: l})} className={`w-6 h-7 rounded text-[9px] font-bold ${f.stress_level===l ? (l<=3?'bg-emerald-600':l<=6?'bg-amber-500':'bg-rose-600')+' text-white' : 'bg-white border text-slate-500'}`}>{l}</button>
              ))}</div>
            </div>
          </div>

          {/* Activity */}
          <div className="bg-emerald-50 rounded-lg p-3 space-y-2">
            <p className="text-xs font-bold text-emerald-700 flex items-center gap-1"><Dumbbell className="w-3.5 h-3.5" /> Activity</p>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-[10px]">Steps</Label><Input type="number" value={f.steps||''} onChange={e => setF({...f, steps: parseInt(e.target.value)||null})} placeholder="5000" className="h-8 text-xs mt-0.5" /></div>
              <div><Label className="text-[10px]">Minutes</Label><Input type="number" value={f.duration_minutes||''} onChange={e => setF({...f, duration_minutes: parseInt(e.target.value)||null})} placeholder="30" className="h-8 text-xs mt-0.5" /></div>
              <div><Label className="text-[10px]">Cal Burned</Label><Input type="number" value={f.calories_burned||''} onChange={e => setF({...f, calories_burned: parseInt(e.target.value)||null})} placeholder="200" className="h-8 text-xs mt-0.5" /></div>
            </div>
          </div>

          {/* Nutrition */}
          <div className="bg-orange-50 rounded-lg p-3 space-y-2">
            <p className="text-xs font-bold text-orange-700 flex items-center gap-1"><Utensils className="w-3.5 h-3.5" /> Meal (optional)</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2"><Input value={f.food_items||''} onChange={e => setF({...f, food_items: e.target.value})} placeholder="What did you eat?" className="h-8 text-xs" /></div>
              <div>
                <select value={f.meal_type||'lunch'} onChange={e => setF({...f, meal_type: e.target.value})} className="w-full h-8 rounded-md border border-input bg-background px-2 text-xs">
                  {['breakfast','lunch','dinner','snack'].map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div><Input type="number" value={f.calories||''} onChange={e => setF({...f, calories: parseInt(e.target.value)||null})} placeholder="Calories" className="h-8 text-xs" /></div>
            </div>
          </div>

          <Button onClick={() => onSave({...f, date: todayStr})} disabled={saving} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Zap className="w-4 h-4 mr-1" />} Log Everything
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ==================== CONNECTED APPS ====================
function ConnectedApps({ onSync }) {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_FIT_CLIENT_ID;
  const REDIRECT_URI = window.location.origin + '/patient/dashboard';
  const SCOPES = 'https://www.googleapis.com/auth/fitness.activity.read https://www.googleapis.com/auth/fitness.body.read https://www.googleapis.com/auth/fitness.sleep.read https://www.googleapis.com/auth/fitness.heart_rate.read';

  // Check connection status on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/google-fit/status`, { headers: getHeaders() });
        if (res.ok) { const d = await res.json(); setConnected(d.connected); }
      } catch {}
      setLoading(false);
    })();
  }, []);

  // Handle OAuth callback code from URL — single use only
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const alreadyHandled = sessionStorage.getItem('gfit_code_handled');
    if (code && !connected && alreadyHandled !== code) {
      sessionStorage.setItem('gfit_code_handled', code);
      // Clean URL immediately to prevent reuse
      window.history.replaceState({}, '', window.location.pathname);
      (async () => {
        setLoading(true);
        try {
          const res = await fetch(`${API_URL}/api/google-fit/exchange-token`, {
            method: 'POST', headers: getHeaders(),
            body: JSON.stringify({ code, redirect_uri: REDIRECT_URI })
          });
          if (res.ok) {
            setConnected(true);
            toast.success('Google Fit connected successfully!');
          } else {
            const d = await res.json();
            toast.error(d.detail || 'Connection failed. Please try again.');
          }
        } catch { toast.error('Connection failed'); }
        setLoading(false);
      })();
    }
  }, []);

  const connectGoogleFit = () => {
    const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&response_type=code&scope=${encodeURIComponent(SCOPES)}&access_type=offline&prompt=consent`;
    window.location.href = url;
  };

  const disconnectGoogleFit = async () => {
    try {
      await fetch(`${API_URL}/api/google-fit/disconnect`, { method: 'POST', headers: getHeaders() });
      setConnected(false);
      toast.success('Google Fit disconnected');
    } catch {}
  };

  const syncNow = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await fetch(`${API_URL}/api/google-fit/sync`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify({ days: 7 })
      });
      if (res.ok) {
        const d = await res.json();
        setSyncResult(d);
        toast.success(d.message);
        if (onSync) onSync();
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Sync failed');
      }
    } catch { toast.error('Sync failed'); }
    setSyncing(false);
  };

  if (loading) return <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal-600 mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="connected-apps">
      <h3 className="text-sm font-bold text-slate-700">Google Fit Integration</h3>
      <p className="text-xs text-slate-500">Connect Google Fit to auto-import steps, calories, sleep & heart rate</p>

      <Card className={`border-2 ${connected ? 'border-emerald-300 bg-emerald-50/30' : 'border-slate-200'}`}>
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 via-green-500 to-yellow-500 flex items-center justify-center shadow-md">
              <Heart className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-base font-bold text-slate-800">Google Fit</p>
              <p className="text-xs text-slate-500">Steps, Calories, Sleep, Heart Rate</p>
            </div>
            {connected ? (
              <Badge className="bg-emerald-100 text-emerald-700 text-xs px-3 py-1">Connected</Badge>
            ) : (
              <Badge variant="outline" className="text-xs">Not Connected</Badge>
            )}
          </div>

          {connected ? (
            <div className="space-y-3">
              <div className="bg-white border border-emerald-200 rounded-lg p-3">
                <p className="text-xs text-emerald-700 font-medium">Your Google Fit account is connected</p>
                <p className="text-[10px] text-emerald-600 mt-0.5">Click "Sync Now" to import your latest health data</p>
              </div>

              <div className="flex gap-2">
                <Button onClick={syncNow} disabled={syncing} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="google-fit-sync-btn">
                  {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <TrendingUp className="w-4 h-4 mr-1.5" />}
                  {syncing ? 'Syncing...' : 'Sync Now (Last 7 Days)'}
                </Button>
                <Button variant="outline" size="sm" onClick={disconnectGoogleFit} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="google-fit-disconnect-btn">
                  Disconnect
                </Button>
              </div>

              {syncResult && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                  <p className="text-xs text-blue-700">{syncResult.message}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-1.5">
                <p className="text-xs text-blue-700 font-medium">What gets synced:</p>
                <div className="grid grid-cols-2 gap-1.5 text-[11px] text-blue-600">
                  <span className="flex items-center gap-1"><Footprints className="w-3 h-3" /> Daily Steps</span>
                  <span className="flex items-center gap-1"><Flame className="w-3 h-3" /> Calories Burned</span>
                  <span className="flex items-center gap-1"><BedDouble className="w-3 h-3" /> Sleep Duration</span>
                  <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> Heart Rate</span>
                </div>
              </div>

              <Button onClick={connectGoogleFit} className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium" data-testid="google-fit-connect-btn">
                <Smartphone className="w-5 h-5 mr-2" /> Connect Google Fit
              </Button>

              <p className="text-[10px] text-slate-400 text-center">You'll be redirected to Google to authorize access to your fitness data</p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
        <p className="text-xs text-teal-700 font-medium">Also try AI Quick Log</p>
        <p className="text-[10px] text-teal-600 mt-0.5">Go to Overview → type or speak "slept 7h, walked 5000 steps, had eggs toast" and AI creates all entries</p>
      </div>
    </div>
  );
}
